import { CloudUpload, FileImage, Trash2 } from 'lucide-react';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import Button from '@/shared/components/ui/Button';
import { showToast } from '@/shared/utils/showToast';

interface UploadTransferProofModalProps {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  /** Receives the chosen image and a preview URL that stays valid after the modal closes */
  onUpload?: (file: File, previewUrl: string) => void;
}

const ACCEPT = 'image/jpeg,image/png';
const MAX_SIZE = 5 * 1024 * 1024;

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** Upload Bukti Transfer: empty dropzone first, then the chosen file with its preview. */
const UploadTransferProofModal = ({
  open,
  loading,
  onClose,
  onUpload,
}: UploadTransferProofModalProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // The preview URL is handed to onUpload, so only unused ones are revoked
  const handedOver = useRef(false);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    handedOver.current = false;
    setPreviewUrl(url);
    return () => {
      if (!handedOver.current) URL.revokeObjectURL(url);
    };
  }, [file]);

  // Every open starts empty
  useEffect(() => {
    if (!open) setFile(null);
  }, [open]);

  if (!open) return null;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0];
    event.target.value = '';
    if (!chosen) return;
    if (!ACCEPT.split(',').includes(chosen.type)) {
      showToast({
        type: 'error',
        title: 'Format tidak didukung',
        message: 'Gunakan file JPG atau PNG.',
      });
      return;
    }
    if (chosen.size > MAX_SIZE) {
      showToast({ type: 'error', title: 'File terlalu besar', message: 'Ukuran maksimal 5 MB.' });
      return;
    }
    setFile(chosen);
  };

  const handleUpload = () => {
    if (!file || !previewUrl) return;
    handedOver.current = true;
    onUpload?.(file, previewUrl);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upload-transfer-proof-title"
    >
      <button
        type="button"
        aria-label="Batal"
        className="absolute inset-0 bg-black/50 cursor-default"
        disabled={loading}
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-[420px] rounded-xl bg-white p-6 flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <p id="upload-transfer-proof-title" className="title1bold">
            Upload Bukti Transfer
          </p>
          <p className="body1 text-text-grey">
            Unggah bukti transfer sebagai konfirmasi bahwa pembayaran telah dilakukan.
          </p>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden!"
          onChange={handleChange}
          disabled={loading}
        />

        {file && previewUrl ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3 rounded-[8px] border border-blue-cs-200 px-3 py-2">
              <FileImage size={20} strokeWidth={1.8} className="shrink-0 text-black" />
              <span className="body1medium truncate min-w-0 flex-1" title={file.name}>
                {file.name}
              </span>
              <span className="body2 text-text-grey shrink-0">{formatSize(file.size)}</span>
              <button
                type="button"
                aria-label="Hapus file"
                onClick={() => setFile(null)}
                disabled={loading}
                className="shrink-0 text-[#FF3B30] transition hover:opacity-80 disabled:opacity-50"
              >
                <Trash2 size={18} strokeWidth={1.8} />
              </button>
            </div>
            <div className="max-h-[320px] overflow-auto rounded-[8px] border border-blue-cs-200 bg-[#F6F6F6]">
              <img
                src={previewUrl}
                alt="Pratinjau bukti transfer"
                className="w-full object-contain"
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 rounded-[8px] border border-dashed border-blue-cs-200 bg-[#F6F6F6] px-4 py-6 text-center">
            <CloudUpload size={40} strokeWidth={1.5} className="text-black" />
            <p className="body1bold">Unggah bukti transfer</p>
            <p className="body2 text-text-grey">Format JPG,PNG, Maksimal 5 MB.</p>
            <Button
              customButton="btn-fill-white-db mt-1"
              onClick={() => inputRef.current?.click()}
              disabled={loading}
            >
              Pilih File
            </Button>
          </div>
        )}

        <div className="flex gap-3">
          <Button className="btn-fill-white-db flex-1" disabled={loading} onClick={onClose}>
            Batal
          </Button>
          <Button
            className="btn-fill-blue flex-1"
            disabled={loading || !file}
            onClick={handleUpload}
          >
            Upload
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UploadTransferProofModal;
